import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import { api } from "../api/api";
import { platformApi } from "../platform";
import { useCheckoutPayment } from "./useCheckoutPayment";
import type { TemporallyStoreFlightBookingDetailsBodyInputDto } from "../types/codegen";
import {
  PurchaseStatus,
  type PaymentMethod,
  type PaymentResult,
} from "../payments/types";
import { updatePersonalData } from "../utils/bookings";
import { FlightTrackingEvents, rudderAnalytics } from "../rudderstack";

type PaymentParams = TemporallyStoreFlightBookingDetailsBodyInputDto & {
  offerId: string;
};

type BnplPaymentParams = PaymentParams & {
  sessionId: string;
  otp: string;
};

type Props = {
  onSuccess: (result: PaymentResult) => void;
  initialAvailabilityToken?: string;
  method?: PaymentMethod;
};

export const useFlightBooking = ({
  onSuccess,
  initialAvailabilityToken,
  method,
}: Props) => {
  const [checkoutPurchasePending, setCheckoutPurchasePending] = useState(false);

  const { flowComponent, isLoadingForm } = useCheckoutPayment({
    availabilityToken: initialAvailabilityToken,
    serviceType: "air",
    enabled: method === "checkout",
    onSuccess,
    onSubmit: () => setCheckoutPurchasePending(true),
  });

  const handleError = useCallback((error: unknown) => {
    console.error("Error in booking or payment process:", error);
    Sentry.captureException(error);
  }, []);

  const checkoutPayment = {
    submit: async ({ offerId, ...postData }: PaymentParams) => {
      try {
        if (!flowComponent) {
          return;
        }

        const wasSuccess = await updatePersonalData();
        if (!wasSuccess) {
          return {
            orderId: "",
            status: PurchaseStatus.CANCELLED,
          };
        }

        await api.post(`/flights/${offerId}/book`, postData);
        flowComponent.submit();
      } catch (error) {
        handleError(error);
        setCheckoutPurchasePending(false);
      }
    },
    isLoadingCheckoutForm: isLoadingForm,
    isPending: checkoutPurchasePending,
  };

  const qiPayment = useMutation({
    mutationFn: async ({ offerId, ...postData }: PaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(`/flights/${offerId}/book`, postData);

      // Use standard payment flow
      return platformApi.getPaymentProvider("standard").initiatePayment({
        availabilityTokens: [postData.availabilityToken],
        serviceType: "air",
        onSuccess: ({ orderId }) => {
          rudderAnalytics.track(FlightTrackingEvents.USER_SUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "Success",
            transaction_id: orderId,
          });
        },
        onFailure: ({ orderId }) => {
          rudderAnalytics.track(FlightTrackingEvents.USER_UNSUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "fail",
            transaction_id: orderId,
          });
        },
      });
    },
    onSuccess,
    onError: handleError,
  });

  const bnplPayment = useMutation({
    mutationFn: async ({
      offerId,
      sessionId,
      otp,
      ...postData
    }: BnplPaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(`/flights/${offerId}/book`, postData);

      return platformApi.getPaymentProvider("bnpl").initiatePayment({
        availabilityTokens: [postData.availabilityToken],
        sessionId,
        otp,
      });
    },
    onSuccess,
    onError: handleError,
  });

  return {
    qiPayment,
    checkoutPayment,
    bnplPayment,
  };
};
